//************************************************************************ /
// RSVP LIST CARD
// White card in the user nav menu that lists the 5 most recent RSVPs for the event,
// with the pagination option to view the next/previous 5.
//************************************************************************ /

import React, { memo, useCallback, useEffect, useState } from "react";
import { Member } from "@dreambigger/shared/src/types/event";
import {
  Card,
  Divider,
  Avatar,
  NextAndPreviousBar,
  message,
  Spin,
} from "@dreambigger/design-system/src/components";
import { generateCloudinaryUrl } from "@dreambigger/shared/src/utils";
import { getIcon } from "@dreambigger/design-system/src/utils";
import { eventApi } from "@dreambigger/shared/src/api/weserve/api-helper";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";
import { PaginatedAttendees } from "@dreambigger/shared/src/api/events";

const PAGE_SIZE = 5;

type Props = {
  eventId: number;
};

const AttendeeCard = ({ eventId }: Props) => {
  const [attendees, setAttendees] = useState<PaginatedAttendees["records"]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>();

  useEffect(() => {
    eventApi
      .getAttendees(
        eventId,
        generateWeserveQueryParams({
          offset: String(offset),
          limit: String(PAGE_SIZE),
          sort: "created_at",
          direction: "desc",
        })
      )
      .then((res) => {
        setAttendees(res.data.records);
        setTotalRecords(res.data.totalRecords);
      })
      .catch(() => message.error("Something went wrong loading the attendees"))
      .finally(() => {
        setLoading(false);
      });
  }, [offset, eventId]);

  const getPreviousFive = useCallback(() => {
    setLoading(true);

    setOffset((oldOffset) => {
      if (oldOffset < PAGE_SIZE) {
        // We don't want to return a negative offset, so just return 0.
        return 0;
      }
      return oldOffset - PAGE_SIZE;
    });
  }, [offset]);

  const getNextFive = useCallback(() => {
    setLoading(true);

    setOffset((oldOffset) => {
      return oldOffset + PAGE_SIZE;
    });
  }, [offset]);

  const handleDownloadRSVPs = useCallback(() => {
    if (loadingDownload) return;
    setLoadingDownload(true);
    eventApi
      .triggerRsvpDownload(eventId)
      .then(() => {
        message.success("Your RSVP list has been emailed to you");
      })
      .catch(() => message.error("Failed to trigger a attendee download"))
      .finally(() => setLoadingDownload(false));
  }, [eventId]);

  return (
    <Card
      style={{
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        backgroundColor: "white",
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div className="flex flex-column" style={{ padding: "26px" }}>
        <div className="flex justify-between">
          <h4 className="f-4">Attending</h4>
          {attendees.length !== 0 && (
            <div
              className="primary underline subtleGrow pointer"
              onClick={handleDownloadRSVPs}
            >
              {getIcon("download")} Download List
            </div>
          )}
        </div>
        {attendees.length === 0 && loading && <Spin className="tl mt-2" />}
        {attendees.length === 0 && !loading && <span>No Attendees Yet</span>}
        {attendees.map((attendee) => (
          <div key={attendee.id} className="flex items-center">
            <div className="mr-4 mb-2">
              <AttendeeAvatar attendee={attendee.user} />
            </div>
            <div className="fwb">{`${attendee.user.firstName} ${attendee.user.lastName}`}</div>
          </div>
        ))}
      </div>
      {/* Only show if we have more records to fetch. */}
      {totalRecords && totalRecords > PAGE_SIZE && (
        <>
          <Divider />
          <NextAndPreviousBar
            loading={loading}
            className="pa-4"
            actionTextClass="primary ttu"
            previousText="Last Five"
            nextText="Next Five"
            showLeft={offset !== 0}
            showRight={totalRecords > offset + PAGE_SIZE}
            nextFunction={getNextFive}
            previousFunction={getPreviousFive}
          />
        </>
      )}
    </Card>
  );
};

const AttendeeAvatar: React.FC<{ attendee: Member }> = ({ attendee }) => {
  const userInitials = (
    (attendee.firstName[0] ?? "") + (attendee?.lastName[0] ?? "")
  ).toUpperCase();

  // We don't want to use the default avatar image that is saved to users.
  const hasUserImage =
    attendee.imageId && attendee.imageVersion && attendee.imageId !== "avatar";

  return (
    <Avatar
      size="large"
      alt="user avatar"
      src={
        hasUserImage &&
        generateCloudinaryUrl(attendee.imageVersion!, attendee.imageId!, {
          type: "avatar",
        })
      }
    >
      {!hasUserImage && <div className="usn">{userInitials}</div>}
    </Avatar>
  );
};

export default memo(AttendeeCard);
